/* istanbul ignore file */
import React, { useEffect } from 'react';
import { batch } from 'react-redux';
import { useCurator } from '@noths/curator-web';
import type { CookieObj } from '@noths/polaris-client-gdpr-compliance';
import { CookieModal, getCookieConsent } from '@noths/polaris-client-gdpr-compliance';
import { GTMWithDataLayer } from '@noths/polaris-client-google-analytics';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { generateRandomString } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import { StatusCode } from '@noths/polaris-dev-ts-types';
import { Environment } from '@noths/polaris-server-content-fetching';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { parse } from 'url';

import { SupportShowInCMSBookmarklet } from 'src/components/atoms/SupportShowInCMSBookmarklet/SupportShowInCMSBookmarklet';
import { StandardLayout } from 'src/components/layouts/Standard/StandardLayout';
import { ContactPartnerModalContainer } from 'src/components/molecules/ContactPartnerModal/ContactPartnerModalContainer';
import { PageErrorMessage } from 'src/components/molecules/PageErrorMessage/PageErrorMessage';
import { BasketModalContainer } from 'src/components/organisms/BasketModal/containers/BasketModalContainer';
import { ERROR_PRODUCT_NOT_FOUND } from 'src/constants/errors';
import { isProd } from 'src/environment';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import { getProductJSONLDSchema } from 'src/JSONLDSchema/getProductJSONLDSchema';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { adaptContentstackContent } from 'src/redux/content/adapters/adaptContentstackContent';
import { actions as contentActions } from 'src/redux/content/slice';
import { actions as deliveryActions } from 'src/redux/delivery/slice';
import { requestDeliveryZoneOptions } from 'src/redux/delivery/thunks';
import { setPartnerInsights } from 'src/redux/partner/slice';
import { requestProductDetails } from 'src/redux/product/thunks';
import { requestProductReviews } from 'src/redux/reviews/thunks';
import type { AppThunkDispatch } from 'src/redux/store';
import { getStore } from 'src/redux/store';
import type { CookieConsent } from 'src/redux/user/slice';
import { actions as userActions } from 'src/redux/user/slice';
import { fetchGlobalContent } from 'src/services/contentstack';
import { fetchPageContent } from 'src/services/contentstack/fetchPageContent';
import { fetchPartnerInsights } from 'src/services/partner-product-insights-api/fetchPartnerInsights';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';
import { getServerSidePageEventObject } from 'src/utils/gaPageEvent';
import { isOutOfStock } from 'src/utils/inventoryStatus';
import { getIsRequestFromExternalReferrer } from 'src/utils/page';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

const { appVersion, version } = publicRuntimeConfig;

const Curator = dynamic(
  () =>
    import('@noths/curator-web').then(async (m) => {
      m.configureCurator({
        env: isProd() ? 'production' : 'qa',
        hideChatToggleForMobileViewport: true,
      });
      return m.Curator;
    }),
  { ssr: false },
);

export const getServerSideProps: GetServerSideProps<PageProps> = async ({ query, req, res }) => {
  const partnerId = Array.isArray(query.partnerId) ? query.partnerId[0] : query.partnerId ?? null;
  const pathname = parse(req.url!).pathname!;
  const domain = publicRuntimeConfig.hosts.domain;
  const canonicalUrl = `https://${domain}${pathname}`;
  const externalReferrer = getIsRequestFromExternalReferrer(req, query);

  const {
    countryCode: initialCountryCode,
    currency: initialCurrency,
    deliveryZone: initialDeliveryZone,
  } = parseLocaleSetCookieHeaders(res.getHeaders());

  const { _ga: googleAnalyticsVisitorId, country_code, currency, delivery_zone } = req.cookies;
  const cookieConsent: CookieConsent = getCookieConsent(req.cookies as CookieObj);
  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};
  const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);

  const countryCode = initialCountryCode ?? country_code;
  const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
  const deliveryZone = (initialDeliveryZone ?? delivery_zone) as DeliveryZoneCode;

  const { dispatch, getState } = getStore(
    {
      router: { pathname, query },
    },
    {
      user: {
        bot: getMvtStrategyFromRequest(req) === 'bot',
        countryCode,
        currencyCode,
        gaVisitorId,
      },
    },
  );

  batch(() => {
    dispatch(userActions.setCookieConsent(cookieConsent));
    dispatch(userActions.setDeliveryZone(deliveryZone));
    dispatch(userActions.setFeatureFlags(featureFlags));
    dispatch(deliveryActions.setSelectedCountry(countryCode));
    dispatch(deliveryActions.setSelectedZone(deliveryZone));
  });

  const [, [siteChromeContent], pageContent, partnerInsights] = await Promise.all([
    dispatch(
      requestProductDetails({
        cookie: req.headers.cookie,
        healthcheck: req.headers['health-check'] === 'true',
        previewing: !!query.previewing,
        productURI: pathname,
      }),
    ),
    fetchGlobalContent({ featureFlags }),
    fetchPageContent(Environment.Production).catch(() => null),
    partnerId ? fetchPartnerInsights(partnerId).catch(() => null) : null,
  ]);

  dispatch(setPartnerInsights(partnerInsights));

  if (pageContent) {
    const contentState = adaptContentstackContent(pageContent);

    dispatch(contentActions.setContent(contentState));
  }

  // Get initial state to check for redirects/errors
  let state: ReduxApplicationState = getState();
  const { product: initialProduct } = state;

  if (initialProduct.redirectUrl) {
    return {
      redirect: {
        destination: initialProduct.redirectUrl,
        statusCode: StatusCode.MovedPermanently,
      },
    };
  }

  if (initialProduct.error) {
    res.statusCode =
      initialProduct.error.name === ERROR_PRODUCT_NOT_FOUND
        ? StatusCode.NotFound
        : StatusCode.InternalServerError;

    if (res.statusCode === StatusCode.InternalServerError) {
      res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
    }
  }

  if (
    initialProduct.code &&
    !isOutOfStock(initialProduct.inventoryStatus) &&
    initialProduct.purchasable
  ) {
    await (dispatch as AppThunkDispatch)(
      requestDeliveryZoneOptions({
        productCode: initialProduct.code,
        productPrice: initialProduct.price,
      }),
    );
  }

  // Get final state after all async operations, including delivery options
  state = getState();
  const { basket, content, delivery, partner, product, productImagesCarousel, reviews, user } =
    state;
  const props: PageProps = {
    canonicalUrl,
    enableCurator: featureFlags?.curator_web === 'enabled' ?? false,
    siteChromeContent,
    externalReferrer,
    updatedReduxState: {
      // Cherry pick the state to be updated on the client
      basket,
      content,
      delivery,
      partner,
      product,
      productImagesCarousel,
      reviews,
      user,
    },
  };

  return { props };
};

export default (props: PageProps) => {
  const { dispatch } = getStore();
  const { canonicalUrl, externalReferrer, updatedReduxState } = props;
  const { basket, delivery, partner, product, user } = updatedReduxState;
  const productJSONLDSchema = getProductJSONLDSchema(product, delivery, user.featureFlags);
  const pageEventData =
    !!updatedReduxState?.product && !!updatedReduxState?.basket
      ? getServerSidePageEventObject({
          partnerAverageProductRating: partner.insights?.averageProductRating ?? undefined,
          version,
          appVersion,
          externalReferrer,
          layoutType: 'standard',
          optionsCount: basket.options.length,
          productState: product,
          requestIsFromBot: updatedReduxState.user.bot,
          selectedOptionValues: basket.selectedOptionValues,
        })
      : {};

  useEffect(() => {
    if (product.code) {
      dispatch(requestProductReviews({ autoInitiated: true, page: 1, productCode: product.code }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { handleToggle, isCuratorOpen, redirectToPDP } = useCurator();
  const showCurator = props.enableCurator ?? false;

  return (
    <>
      <Head>
        <title>{`${product.title} By ${product.partner.name}`}</title>
        <meta content={product.metaDescription} name="description" />
        {!product.metaRobotsIndexable && <meta content="noindex,follow" name="robots" />}
        <link href={canonicalUrl} rel="canonical" />
        {productJSONLDSchema && (
          <script
            dangerouslySetInnerHTML={{ __html: JSON.stringify(productJSONLDSchema) }}
            type="application/ld+json"
          />
        )}
        <CookieModal useTestScript={!isProd()} />
      </Head>
      <GTMWithDataLayer pageEventData={pageEventData} />
      {product.error ? (
        <PageErrorMessage pageNotFound={product.error.name === ERROR_PRODUCT_NOT_FOUND} />
      ) : (
        <>
          <StandardLayout />
          <BasketModalContainer />
          <ContactPartnerModalContainer />
          <SupportShowInCMSBookmarklet productCode={product.code} />
        </>
      )}
      {showCurator && (
        <Curator
          isOpen={isCuratorOpen}
          onProductClick={redirectToPDP}
          onToggle={handleToggle}
          showFavourties
        />
      )}
    </>
  );
};
